// src/pages/UnifiedDashboard.tsx
import React from 'react';
import {
  BarChart3,
  BookOpen,
  Brain,
  Building2,
  CreditCard,
  FolderOpen,
  Inbox,
  LayoutGrid,
  Layers3,
  ListTodo,
  Newspaper,
  Ticket,
  Timer,
  TrendingUp,
  Users,
} from 'lucide-react';
import DashboardShell from '../components/layouts/DashboardShell';
import type { LinkDef } from '../components/sidebar/FloatingSidebar';
import { useAuth } from '../context/AuthProvider';
import StaffTicketPortalHost from '../components/tickets/StaffTicketPortalHost';

type AppNavLink = LinkDef & { roles: string[]; children?: AppNavLink[] };
const NAV_ICON_SIZE = 16;

// All possible links with their required roles
const allLinks: AppNavLink[] = [
  {
    to: '/dashboard/overview',
    label: 'Overview',
    icon: <BarChart3 size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['admin', 'agent'],
  },
  {
    to: '/dashboard/kbase',
    label: 'KBase',
    icon: <BookOpen size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['admin', 'agent'],
  },
  {
    to: '/dashboard/publications',
    label: 'Publications',
    icon: <Newspaper size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['admin', 'agent'],
  },
  // Admin directory
  {
    label: 'Admin',
      icon: <Building2 size={NAV_ICON_SIZE} strokeWidth={2} />,
      roles: ['admin'],
      children: [
        {
          to: '/dashboard/users',
          label: 'CRM',
          icon: <Users size={NAV_ICON_SIZE} strokeWidth={2} />,
          roles: ['admin'],
        },
        {
          to: '/dashboard/leads',
          label: 'Leads',
          icon: <Users size={NAV_ICON_SIZE} strokeWidth={2} />,
          roles: ['admin', 'agent'],
        },
        {
          to: '/dashboard/job-board',
          label: 'Job Board',
          icon: <ListTodo size={NAV_ICON_SIZE} strokeWidth={2} />,
          roles: ['admin', 'agent'],
        },
      {
        to: '/dashboard/organization',
        label: 'Brain',
        icon: <Brain size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin'],
      },
      {
        to: '/dashboard/admin/services',
        label: 'Services',
        icon: <Timer size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin'],
      },
    ],
  },
  {
    label: 'Tickets',
    icon: <Ticket size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['admin', 'agent'],
    children: [
      {
        to: '/dashboard/list',
        label: 'List',
        icon: <Ticket size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin', 'agent'],
      },
      {
        to: '/dashboard/board',
        label: 'Board',
        icon: <LayoutGrid size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin', 'agent'],
      },
    ],
  },
  {
    label: 'Teams',
    icon: <Users size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['admin', 'agent'],
    children: [
      {
        to: '/dashboard/teams/overview',
        label: 'Overview',
        icon: <BarChart3 size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin', 'agent'],
      },
      {
        to: '/dashboard/teams/organizations',
        label: 'Organizations',
        icon: <Building2 size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin'],
      },
      {
        to: '/dashboard/teams/projects',
        label: 'Projects',
        icon: <FolderOpen size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin', 'agent'],
      },
      {
        to: '/dashboard/teams/queue',
        label: 'Team Queue',
        icon: <Inbox size={NAV_ICON_SIZE} strokeWidth={2} />,
        roles: ['admin', 'agent'],
      },
    ],
  },
  {
    to: '/dashboard/apps',
    label: 'Apps',
    icon: <LayoutGrid size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['admin', 'agent'],
  },

  // Agents and Admins:
  {
    to: '/dashboard/ticket-queue',
    label: 'Global Queue',
    icon: <Inbox size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['agent', 'admin'],
  },

  // Client (NEW IA)
  {
    to: '/dashboard/home',
    label: 'Dashboard',
    icon: <BarChart3 size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['client'],
  },
  {
    to: '/dashboard/billing',
    label: 'Billing',
    icon: <CreditCard size={NAV_ICON_SIZE} strokeWidth={2} />,
    roles: ['client'],
  },
];

export default function UnifiedDashboard() {
  const { role, isPaid, isPrimaryAccountHolder } = useAuth();

  if (role === 'client') {
    const clientLinks: LinkDef[] = [
      {
        to: '/dashboard/home',
        label: 'Dashboard',
        icon: <BarChart3 size={NAV_ICON_SIZE} strokeWidth={2} />,
      },
      {
        to: '/dashboard/profile',
        label: 'Profile',
        icon: <Users size={NAV_ICON_SIZE} strokeWidth={2} />,
      },
      {
        to: '/dashboard/projects',
        label: 'Projects',
        icon: <FolderOpen size={NAV_ICON_SIZE} strokeWidth={2} />,
      },
      {
        label: 'Business',
        icon: <Building2 size={NAV_ICON_SIZE} strokeWidth={2} />,
        children: [
          ...(isPrimaryAccountHolder
            ? [
                {
                  to: '/dashboard/business',
                  label: 'Overview',
                  icon: <Building2 size={NAV_ICON_SIZE} strokeWidth={2} />,
                } satisfies LinkDef,
              ]
            : []),
          {
            to: '/dashboard/services',
            label: 'Services',
            icon: <Layers3 size={NAV_ICON_SIZE} strokeWidth={2} />,
          },
          {
            to: '/dashboard/billing',
            label: 'Billing',
            icon: <CreditCard size={NAV_ICON_SIZE} strokeWidth={2} />,
          },
        ],
      },
    ];

    return <DashboardShell links={clientLinks} mobileSidebarWidth="75vw" />;
  }

  const mapLinksForRole = (links: AppNavLink[]): LinkDef[] =>
    links
      .filter((link) => link.roles.includes(role || ''))
      .map((link) => {
        const { roles, children, ...rest } = link;

        if (children?.length) {
          const nextChildren = mapLinksForRole(children);
          if (!nextChildren.length) return null;
          return {
            ...rest,
            children: nextChildren,
          } satisfies LinkDef;
        }

        if (!rest.to) return null;
        return rest satisfies LinkDef;
      })
      .filter((link): link is LinkDef => Boolean(link));

  const userLinks: LinkDef[] = mapLinksForRole(allLinks);

  // Floating CTA: create vs upgrade
  const createTicketLink: LinkDef & { to: string } = isPaid
    ? {
        to: '/dashboard/tickets/new',
        label: 'Create Ticket',
        icon: <Ticket size={NAV_ICON_SIZE} strokeWidth={2} />,
      }
    : {
        to: '/dashboard/billing',
        label: 'Upgrade to Create',
        icon: <TrendingUp size={NAV_ICON_SIZE} strokeWidth={2} />,
      };

  return (
    <>
      <DashboardShell links={userLinks} createTicketLink={createTicketLink} />
      <StaffTicketPortalHost />
    </>
  );
}
